/**
 * Mailvlak (sessie 75, blok 3): de mail van de klant tonen zoals hij is
 * verstuurd. De server (`app/services/mail_weergave.py`) levert al
 * geschoonde body-HTML; hier komt die in een sandbox-iframe met een eigen
 * document en een strakke CSP. Twee grendels dus:
 *
 * - sandbox zonder allow-scripts: in het vlak draait nooit code;
 * - CSP in het document: alleen data:- en blob:-afbeeldingen, en https:
 *   pas nadat iemand op "Afbeeldingen laden" klikt (de knop staat in
 *   `MailWeergave`, die ook het eerdere bericht inklapt; sessie 186).
 *
 * Inline-afbeeldingen (cid) staan als `img[data-cid]` in de HTML. Na het
 * laden van het iframe haalt het vlak die bijlagen op via
 * `emailsApi.bijlageInhoud` en zet een blob-URL als src. Externe
 * afbeeldingen staan als `img[data-extern-src]` en krijgen pas hun src als
 * `toonExtern` aan staat.
 *
 * Het iframe groeit mee met de inhoud; brede mails scrollen horizontaal
 * binnen het vlak (de rol eromheen), nooit de pagina.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { emailsApi } from "@/lib/api";

import "./mailvlak.css";

export interface MailvlakProps {
  emailId: string;
  html: string;
  /** Externe afbeeldingen laden (na de klik op "Afbeeldingen laden"). */
  toonExtern: boolean;
  /** Toegankelijke naam van het iframe. */
  titel?: string;
}

export const MAILVLAK_SANDBOX =
  "allow-same-origin allow-popups allow-popups-to-escape-sandbox";

const BASISSTIJL = `
html,body{margin:0;padding:0;background:#fff;color:#111;overflow:hidden}
#vo-inhoud{display:flow-root;overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch}
body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.45;overflow-wrap:anywhere;word-break:normal;-webkit-text-size-adjust:100%}
img{max-width:100%;height:auto}
table{max-width:100%}
a{color:#0b57d0}
blockquote{margin:0 0 0 8px;padding-left:10px;border-left:2px solid #ccc}
pre{white-space:pre-wrap}
.vo-doorgestuurd{margin:12px 0 0 4px;padding-left:12px;border-left:3px solid #d0d0d0}
.vo-doorgestuurd-kop{font-size:12px;color:#666;margin-bottom:8px}
`;

/** Het volledige document voor `srcDoc`. Puur, dus los te testen. */
export function bouwDocument(html: string, toonExtern: boolean): string {
  const imgSrc = `data: blob:${toonExtern ? " https:" : ""}`;
  const csp = `default-src 'none'; img-src ${imgSrc}; style-src 'unsafe-inline'; font-src data:`;
  return (
    "<!DOCTYPE html><html><head>" +
    '<meta charset="utf-8">' +
    `<meta http-equiv="Content-Security-Policy" content="${csp}">` +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    '<base target="_blank">' +
    `<style>${BASISSTIJL}</style>` +
    `</head><body><div id="vo-inhoud">${html}</div></body></html>`
  );
}

/**
 * De hoogte van de inhoud zelf (niet van het venster): zo krimpt het vlak
 * ook weer, bijvoorbeeld na het draaien van de iPhone. Een brede mail
 * scrollt horizontaal in `#vo-inhoud`; die balk telt mee in offsetHeight.
 */
function meetHoogte(doc: Document): number {
  const inhoud = doc.getElementById("vo-inhoud");
  if (inhoud) return Math.ceil(inhoud.offsetHeight);
  return doc.body?.scrollHeight ?? 0;
}

export function Mailvlak({ emailId, html, toonExtern, titel = "Mail van de klant" }: MailvlakProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [hoogte, setHoogte] = useState<number>(0);

  // Blob-URL's per bijlage, hergebruikt als het document opnieuw laadt.
  const blobs = useRef<Map<string, string>>(new Map());
  const levend = useRef(true);
  const waarnemer = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    levend.current = true;
    const kaart = blobs.current;
    return () => {
      levend.current = false;
      waarnemer.current?.disconnect();
      for (const url of kaart.values()) URL.revokeObjectURL(url);
      kaart.clear();
    };
  }, [emailId]);

  const bijwerkenHoogte = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const h = meetHoogte(doc);
    if (h > 0) setHoogte((oud) => (oud === h ? oud : h));
  }, []);

  const onLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    // Hoogte: ResizeObserver op het document, met terugval op scrollHeight.
    waarnemer.current?.disconnect();
    waarnemer.current = null;
    const inhoud = doc.getElementById("vo-inhoud") ?? doc.documentElement;
    if (typeof ResizeObserver !== "undefined" && inhoud) {
      const ro = new ResizeObserver(() => bijwerkenHoogte());
      ro.observe(inhoud);
      waarnemer.current = ro;
    }
    bijwerkenHoogte();
    // Terugval als de waarnemer over de iframe-grens niet afgaat (WebKit):
    // draaien of breder worden van het venster, laatste lettertypes, en
    // een paar late metingen.
    iframeRef.current?.contentWindow?.addEventListener("resize", bijwerkenHoogte);
    void doc.fonts?.ready.then(bijwerkenHoogte);
    for (const ms of [150, 600, 1500]) window.setTimeout(bijwerkenHoogte, ms);

    const imgs = Array.from(doc.querySelectorAll("img"));
    for (const img of imgs) {
      img.addEventListener("load", bijwerkenHoogte);
      img.addEventListener("error", bijwerkenHoogte);
    }

    if (toonExtern) {
      for (const img of Array.from(
        doc.querySelectorAll<HTMLImageElement>("img[data-extern-src]"),
      )) {
        const src = img.getAttribute("data-extern-src") ?? "";
        if (/^https:/i.test(src)) img.setAttribute("src", src);
      }
    }

    const cidImgs = Array.from(
      doc.querySelectorAll<HTMLImageElement>("img[data-cid]"),
    );
    const perBijlage = new Map<string, HTMLImageElement[]>();
    for (const img of cidImgs) {
      const id = img.getAttribute("data-cid");
      if (!id) continue;
      const lijst = perBijlage.get(id) ?? [];
      lijst.push(img);
      perBijlage.set(id, lijst);
    }
    for (const [bijlageId, lijst] of perBijlage) {
      const bekend = blobs.current.get(bijlageId);
      if (bekend) {
        for (const img of lijst) img.setAttribute("src", bekend);
        continue;
      }
      emailsApi
        .bijlageInhoud(emailId, bijlageId)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          if (!levend.current) {
            URL.revokeObjectURL(url);
            return;
          }
          const al = blobs.current.get(bijlageId);
          if (al) {
            URL.revokeObjectURL(url);
          } else {
            blobs.current.set(bijlageId, url);
          }
          for (const img of lijst) img.setAttribute("src", al ?? url);
        })
        .catch(() => {
          // Bijlage niet op te halen: de afbeelding blijft leeg, de mail staat er.
        });
    }
  }, [bijwerkenHoogte, emailId, toonExtern]);

  return (
    <div className="vo-mailvlak">
      <div className="vo-mailvlak-rol">
        <iframe
          ref={iframeRef}
          className="vo-mailvlak-iframe"
          title={titel}
          sandbox={MAILVLAK_SANDBOX}
          srcDoc={bouwDocument(html, toonExtern)}
          onLoad={onLoad}
          style={hoogte > 0 ? { height: `${hoogte}px` } : undefined}
        />
      </div>
    </div>
  );
}

export default Mailvlak;
